import { doc, getDoc, setDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import { firestore } from '../config/firebase';

export interface MindNode {
  id: string;
  text: string;
  x: number;
  y: number;
  width: number;
  height: number;
  color?: string;
  fontSize?: number;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  parentId?: string;
}

export interface StickyNote {
  id: string;
  text: string;
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  fontSize?: number;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  fontFamily?: string;
  listStyle?: 'none' | 'bullet';
  author?: string;
}

export interface TextBox {
  id: string;
  text: string;
  x: number;
  y: number;
  color: string;
  fontSize: number;
  bold: boolean;
  italic: boolean;
  underline?: boolean;
}

export interface Shape {
  id: string;
  type: 'rect' | 'circle' | 'square' | 'triangle';
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
}

export interface BoardPath {
  id: string;
  points: number[]; // flat array [x1, y1, x2, y2,...]
  color: string;
  strokeWidth: number;
}

export interface BoardImage {
  id: string;
  url: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface BrainstormingBoard {
  nodes: Record<string, MindNode>;
  stickyNotes: Record<string, StickyNote>;
  textBoxes: Record<string, TextBox>;
  shapes: Record<string, Shape>;
  paths: Record<string, BoardPath>;
  images: Record<string, BoardImage>;
  lastModified: number;
  createdBy: string;
}

export const getBoardDocRef = (teamId: string, boardId: string = 'data') => doc(firestore, 'boards', teamId, 'brainstorming', boardId);

export const subscribeToBoard = (
  teamId: string,
  cb: (data: BrainstormingBoard | null) => void,
  boardId: string = 'data'
) => {
  const ref = getBoardDocRef(teamId, boardId);
  return onSnapshot(ref, (docSnap) => {
    if (docSnap.exists()) {
      cb(docSnap.data() as BrainstormingBoard);
    } else {
      cb(null);
    }
  });
};

export const initBoardIfMissing = async (teamId: string, uid: string, boardId:string='data') => {
  const ref = getBoardDocRef(teamId, boardId);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, {
      nodes: {},
      stickyNotes: {},
      textBoxes: {},
      shapes: {},
      paths: {},
      images: {},
      lastModified: Date.now(),
      createdBy: uid,
    });
  }
};

export const updateBoard = async (
  teamId: string,
  partial: Partial<BrainstormingBoard>,
  boardId: string = 'data'
) => {
  const ref = getBoardDocRef(teamId, boardId);
  await updateDoc(ref, { ...partial, lastModified: Date.now() });
};

// Atomic position update helpers
export const updateStickyNotePosition = async (
  teamId: string,
  noteId: string,
  x: number,
  y: number,
  boardId: string = 'data'
) => {
  const ref = getBoardDocRef(teamId, boardId);
  const pathX = `stickyNotes.${noteId}.x`;
  const pathY = `stickyNotes.${noteId}.y`;
  await updateDoc(ref, { [pathX]: x, [pathY]: y, lastModified: Date.now() });
};

export const updateMindNodePosition = async (
  teamId: string,
  nodeId: string,
  x: number,
  y: number,
  boardId: string = 'data'
) => {
  const ref = getBoardDocRef(teamId, boardId);
  const pathX = `nodes.${nodeId}.x`;
  const pathY = `nodes.${nodeId}.y`;
  await updateDoc(ref, { [pathX]: x, [pathY]: y, lastModified: Date.now() });
};

export const updateMindNodeSize = async (
  teamId: string,
  nodeId: string,
  width: number,
  height: number,
  boardId: string = 'data'
) => {
  const ref = getBoardDocRef(teamId, boardId);
  await updateDoc(ref, {
    [`nodes.${nodeId}.width`]: width,
    [`nodes.${nodeId}.height`]: height,
    lastModified: Date.now(),
  });
};

export const updateMindNodeStyle = async (
  teamId: string,
  nodeId: string,
  partial: { color?: string; fontSize?: number; bold?: boolean; italic?: boolean; underline?: boolean; },
  boardId: string = 'data'
) => {
  const ref = getBoardDocRef(teamId, boardId);
  const updateObj: any = { lastModified: Date.now() };
  if (partial.color !== undefined) updateObj[`nodes.${nodeId}.color`] = partial.color;
  if (partial.fontSize !== undefined) updateObj[`nodes.${nodeId}.fontSize`] = partial.fontSize;
  if (partial.bold !== undefined) updateObj[`nodes.${nodeId}.bold`] = partial.bold;
  if (partial.italic !== undefined) updateObj[`nodes.${nodeId}.italic`] = partial.italic;
  if (partial.underline !== undefined) updateObj[`nodes.${nodeId}.underline`] = partial.underline;
  await updateDoc(ref, updateObj);
};

export const setNodeParent = async (teamId: string, childId: string, parentId: string | null, boardId: string = 'data') => {
  const ref = getBoardDocRef(teamId, boardId);
  await updateDoc(ref, {
    [`nodes.${childId}.parentId`]: parentId,
    lastModified: Date.now(),
  });
};

export const updateStickyColour = async (teamId: string, noteId: string, color: string, boardId: string = 'data') => {
  const ref = getBoardDocRef(teamId, boardId);
  await updateDoc(ref, {
    [`stickyNotes.${noteId}.color`]: color,
    lastModified: Date.now(),
  });
};

export const updateStickySize = async (
  teamId: string,
  noteId: string,
  width: number,
  height: number,
  boardId: string = 'data'
) => {
  const ref = getBoardDocRef(teamId, boardId);
  await updateDoc(ref, {
    [`stickyNotes.${noteId}.width`]: width,
    [`stickyNotes.${noteId}.height`]: height,
    lastModified: Date.now(),
  });
};

export const updateStickyStyle = async (
  teamId: string,
  noteId: string,
  partial: { color?: string; fontSize?: number; bold?: boolean; italic?: boolean; underline?: boolean; fontFamily?: string; listStyle?: 'none' | 'bullet' },
  boardId: string = 'data'
) => {
  const ref = getBoardDocRef(teamId, boardId);
  const updateObj: any = { lastModified: Date.now() };
  if (partial.color) updateObj[`stickyNotes.${noteId}.color`] = partial.color;
  if (partial.fontSize !== undefined) updateObj[`stickyNotes.${noteId}.fontSize`] = partial.fontSize;
  if (partial.bold !== undefined) updateObj[`stickyNotes.${noteId}.bold`] = partial.bold;
  if (partial.italic !== undefined) updateObj[`stickyNotes.${noteId}.italic`] = partial.italic;
  if (partial.underline !== undefined) updateObj[`stickyNotes.${noteId}.underline`] = partial.underline;
  if (partial.fontFamily) updateObj[`stickyNotes.${noteId}.fontFamily`] = partial.fontFamily;
  if (partial.listStyle) updateObj[`stickyNotes.${noteId}.listStyle`] = partial.listStyle;
  await updateDoc(ref, updateObj);
};

export const updateStickyText = async (teamId:string,noteId:string,text:string,boardId:string='data')=>{
  const ref = getBoardDocRef(teamId, boardId);
  await updateDoc(ref,{
    [`stickyNotes.${noteId}.text`]: text,
    lastModified: Date.now(),
  });
};

export const updateTextBoxPosition = async (
  teamId: string,
  boxId: string,
  x: number,
  y: number,
  boardId: string = 'data'
) => {
  const ref = getBoardDocRef(teamId, boardId);
  await updateDoc(ref, {
    [`textBoxes.${boxId}.x`]: x,
    [`textBoxes.${boxId}.y`]: y,
    lastModified: Date.now(),
  });
};

export const updateTextBoxStyle = async (
  teamId: string,
  boxId: string,
  partial: { color?: string; fontSize?: number; bold?: boolean; italic?: boolean; underline?: boolean },
  boardId: string = 'data'
) => {
  const ref = getBoardDocRef(teamId, boardId);
  const updateObj: any = { lastModified: Date.now() };
  if (partial.color !== undefined) updateObj[`textBoxes.${boxId}.color`] = partial.color;
  if (partial.fontSize !== undefined) updateObj[`textBoxes.${boxId}.fontSize`] = partial.fontSize;
  if (partial.bold !== undefined) updateObj[`textBoxes.${boxId}.bold`] = partial.bold;
  if (partial.italic !== undefined) updateObj[`textBoxes.${boxId}.italic`] = partial.italic;
  if (partial.underline !== undefined) updateObj[`textBoxes.${boxId}.underline`] = partial.underline;
  await updateDoc(ref, updateObj);
};

export const updateShapePosition = async (
  teamId: string,
  shapeId: string,
  x: number,
  y: number,
  boardId: string = 'data'
) => {
  const ref = getBoardDocRef(teamId, boardId);
  await updateDoc(ref, {
    [`shapes.${shapeId}.x`]: x,
    [`shapes.${shapeId}.y`]: y,
    lastModified: Date.now(),
  });
};

export const updateShapeStyle = async (
  teamId: string,
  shapeId: string,
  partial: { color?: string; width?: number; height?: number },
  boardId: string = 'data'
) => {
  const ref = getBoardDocRef(teamId, boardId);
  const updateObj: any = { lastModified: Date.now() };
  if (partial.color !== undefined) updateObj[`shapes.${shapeId}.color`] = partial.color;
  if (partial.width !== undefined) updateObj[`shapes.${shapeId}.width`] = partial.width;
  if (partial.height !== undefined) updateObj[`shapes.${shapeId}.height`] = partial.height;
  await updateDoc(ref, updateObj);
};

// Add path helper
export const addPath = async (teamId: string, path: BoardPath, boardId: string = 'data') => {
  const ref = getBoardDocRef(teamId, boardId);
  await updateDoc(ref, {
    [`paths.${path.id}`]: path,
    lastModified: Date.now(),
  });
};

export const addImage = async (teamId: string, img: BoardImage, boardId: string = 'data') => {
  const ref = getBoardDocRef(teamId, boardId);
  await updateDoc(ref, {
    [`images.${img.id}`]: img,
    lastModified: Date.now(),
  });
};

export const updateImagePosition = async (teamId: string, imgId: string, x: number, y: number, boardId: string = 'data') => {
  const ref = getBoardDocRef(teamId, boardId);
  await updateDoc(ref, {
    [`images.${imgId}.x`]: x,
    [`images.${imgId}.y`]: y,
    lastModified: Date.now(),
  });
};

export const updateImageSize = async (
  teamId: string,
  imgId: string,
  width: number,
  height: number,
  boardId: string = 'data'
) => {
  const ref = getBoardDocRef(teamId, boardId);
  await updateDoc(ref, {
    [`images.${imgId}.width`]: width,
    [`images.${imgId}.height`]: height,
    lastModified: Date.now(),
  });
}; 